import {
  defaultEjendeFirmaRelationColumns,
  defaultEjetFirmaRelationColumns,
} from "../../../../../config/firmaConfig";
import { firmaejerskabEjetAfForm } from "../../../../../config/form/firma/firmaejerskabEjetAfFormConfig";
import { firmaejerskabEjerAfForm } from "../../../../../config/form/firma/firmaejerskabEjerAfFormConfig";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import IngenRegistreringer from "../../../person/id/IngenRegistreringer";
import OpretNyButtonBlock from "../../../person/id/OpretNyButtonBlock";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import { FirmaDto } from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import ButtonDownload from "../../../../ButtonDownload";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import { orderBy } from "../../../../../util/orderBy";

export interface IFirmaRelationerType {
  id: string;
  firmaId: string;
  periode: { fraDato: string; tilDatoInklusiv: string };
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];

export default function FirmaRelationer({
  firma,
  invalidateData,
}: {
  firma: FirmaDto;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "BasisWrite");
  const [firmaEjetRelationColumns, setFirmaEjetRelationActiveColumns] =
    useActiveColumns(
      "firma-ejendefirma-active-columns",
      defaultEjendeFirmaRelationColumns,
    );
  const [firmaEjendeRelationColumns, setFirmaEjendeRelationActiveColumns] =
    useActiveColumns(
      "firma-ejetfirma-active-columns",
      defaultEjetFirmaRelationColumns,
    );

  const { firmaejerskabEjendeFirmaNavigation, firmaejerskabEjetFirma } = firma;
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "firmaRelationerSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );
  const firmaRelationer: IFirmaRelationerType[] = (() => {
    const ejende = firmaejerskabEjendeFirmaNavigation?.length
      ? firmaejerskabEjendeFirmaNavigation?.map((f) => {
          return { id: f.id, firmaId: f.ejetFirma.id, periode: f.periode };
        })
      : [];
    const ejet = firmaejerskabEjetFirma?.length
      ? firmaejerskabEjetFirma?.map((f) => {
          return {
            id: f.id,
            firmaId: f.ejendeFirmaNavigation.id,
            periode: f.periode,
          };
        })
      : [];
    return ejende.concat(ejet);
  })();
  const ejetFirmaSorted = orderBy(
    firmaejerskabEjetFirma,
    sortBy.map((s) => s.field),
    sortBy.map((s) => s.direction),
  );

  const ejendeFirmaSorted = orderBy(
    firmaejerskabEjendeFirmaNavigation,
    sortBy.map((s) => s.field),
    sortBy.map((s) => s.direction),
  );

  // event handlers
  function downloadHandlerEjet() {
    tableToXLSX(firmaEjetRelationColumns, ejetFirmaSorted, {
      fileName: `firmaer-ejet-af-for-${firma.firmaNr}`,
      additionalData: {
        Firmanummer: firma.firmaNr,
      },
    });
  }

  function downloadHandlerEjer() {
    tableToXLSX(firmaEjendeRelationColumns, ejendeFirmaSorted, {
      fileName: `firmaer-ejer-af-for-${firma.firmaNr}`,
      additionalData: {
        Firmanummer: firma.firmaNr,
      },
    });
  }

  return (
    <>
      <div className="c-card px-3 pt-2 pb-3 mb-4">
        <h5 className="my-3 badge">Ejet af</h5>

        {firmaejerskabEjetFirma?.length ? (
          <>
            <ModalProvider>
              <ButtonDownload onClick={downloadHandlerEjet} />
              <TableRoot>
                <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
                  <>
                    <th className="indicator"></th>
                    {getHeaderActiveColumns(firmaEjetRelationColumns, {
                      defaultSortBy,
                      setSortBy,
                      sortBy,
                    })}
                  </>
                </TableHead>
                <TableBody>
                  {ejetFirmaSorted.map((ejetFirma, i) => {
                    return (
                      <TableRow
                        key={i}
                        editConfig={
                          canWrite
                            ? firmaejerskabEjetAfForm(
                                firma.id,
                                firmaRelationer,
                                ejetFirma.id,
                              )
                            : undefined
                        }
                        invalidateData={invalidateData}
                      >
                        <td
                          className={`indicator ${
                            periodeIsActive(
                              new Date(ejetFirma.periode.fraDato),
                              new Date(ejetFirma.periode.tilDatoInklusiv),
                            )
                              ? "has-effect"
                              : ""
                          }`}
                        >
                          <ActiveIcon />
                        </td>
                        <>
                          {firmaEjetRelationColumns
                            .filter((col) => col.active)
                            .map((col) => {
                              return (
                                <TD
                                  key={col.field}
                                  col={col}
                                  rowData={ejetFirma}
                                />
                              );
                            })}
                        </>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </TableRoot>
              <ColumnEditorModal
                defaultColumns={defaultEjetFirmaRelationColumns}
                columns={firmaEjetRelationColumns}
                setActiveColumns={setFirmaEjetRelationActiveColumns}
              />
              <OpretNyButtonBlock
                editConfig={
                  canWrite
                    ? firmaejerskabEjetAfForm(firma.id, firmaRelationer)
                    : undefined
                }
                invalidateData={invalidateData}
              />
            </ModalProvider>
          </>
        ) : (
          <IngenRegistreringer
            editConfig={
              canWrite
                ? firmaejerskabEjetAfForm(firma.id, firmaRelationer)
                : undefined
            }
            invalidateData={invalidateData}
          />
        )}
      </div>
      <div className="c-card px-3 pt-2 pb-3">
        <h5 className="my-3 badge">Ejer af</h5>
        {firmaejerskabEjendeFirmaNavigation?.length ? (
          <>
            <ModalProvider>
              <ButtonDownload onClick={downloadHandlerEjer} />
              <TableRoot>
                <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
                  <>
                    <th className="indicator"></th>
                    {getHeaderActiveColumns(firmaEjendeRelationColumns, {
                      defaultSortBy,
                      setSortBy,
                      sortBy,
                    })}
                  </>
                </TableHead>
                <TableBody>
                  {ejendeFirmaSorted.map((ejendeFirma, i) => {
                    return (
                      <TableRow
                        key={i}
                        editConfig={
                          canWrite
                            ? firmaejerskabEjerAfForm(
                                firma.id,
                                firmaRelationer,
                                ejendeFirma.id,
                              )
                            : undefined
                        }
                        invalidateData={invalidateData}
                      >
                        <td
                          className={`indicator ${
                            periodeIsActive(
                              new Date(ejendeFirma.periode.fraDato),
                              new Date(ejendeFirma.periode.tilDatoInklusiv),
                            )
                              ? "has-effect"
                              : ""
                          }`}
                        >
                          <ActiveIcon />
                        </td>
                        <>
                          {firmaEjendeRelationColumns
                            .filter((col) => col.active)
                            .map((col) => {
                              return (
                                <TD
                                  key={col.field}
                                  col={col}
                                  rowData={ejendeFirma}
                                />
                              );
                            })}
                        </>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </TableRoot>
              <ColumnEditorModal
                defaultColumns={defaultEjendeFirmaRelationColumns}
                columns={firmaEjendeRelationColumns}
                setActiveColumns={setFirmaEjendeRelationActiveColumns}
              />
              <OpretNyButtonBlock
                editConfig={
                  canWrite
                    ? firmaejerskabEjerAfForm(firma.id, firmaRelationer)
                    : undefined
                }
                invalidateData={invalidateData}
              />
            </ModalProvider>
          </>
        ) : (
          <IngenRegistreringer
            editConfig={
              canWrite
                ? firmaejerskabEjerAfForm(firma.id, firmaRelationer)
                : undefined
            }
            invalidateData={invalidateData}
          />
        )}
      </div>
    </>
  );
}
